// src/ConsumerApp/Screens/Addresses/DeliveryAddressModal.js

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

// SVGs (from src/assets/icons/)
import X from '../../../assets/icons/X.svg';
import Plus from '../../../assets/icons/Plus.svg';
import MapPin from '../../../assets/icons/MapPin.svg';
import BuildingOffice from '../../../assets/icons/BuildingOffice.svg';
import Trash from '../../../assets/icons/Trash.svg';
//import CheckCircleGreen from '../../../assets/icons/CheckCircleGreen.svg';
// API Hooks
import {
  useGetDefaultDeliveryAddress,
  useSelectDeliveryAddress,
  useDeleteProject,
} from '../../api/projects/project.hooks';
import {
  useGetDeliveryAddresses,
  useCreateDeliveryAddress,
  useUpdateDeliveryAddress,
  useDeleteDeliveryAddress,
} from '../../api/addresses/deliveryAddress.hooks';

import { LocationService } from '../../api/location.service';

export default function DeliveryAddressModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId || '';

  // Initialize Google Maps
  const { isLoaded } = useJsApiLoader({
  id: 'google-map-script',
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
});
// Map References & Drag State
  const mapRef = useRef(null);
  
  // Static center to prevent React re-renders from cancelling the user's drag
  const [defaultCenter] = useState({ lat: 16.5062, lng: 80.648 });
  const [mapInstance, setMapInstance] = useState(null);
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 16.5062, lng: 80.648 });
  const [viewMode, setViewMode] = useState('LIST');
  const [editingAddress, setEditingAddress] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const searchTimeoutRef = useRef(null);
  const [exactCoords, setExactCoords] = useState({ latitude: 16.5062, longitude: 80.648 });
  const [tempAddressData, setTempAddressData] = useState({
    city: '',
    fullAddress: '',
    isFetching: false
  });

  const [formData, setFormData] = useState({
    addressType: 'project',
    projectName: '',
    completeAddress: '',
    building: '',
    landmark: '',
    city: '',
    pincode: '',
    contactName: '',
    contactMobile: '',
  });

  const { data: addressesList = [], isLoading } = useGetDeliveryAddresses(customerId);
  const { data: defaultAddressData } = useGetDefaultDeliveryAddress(customerId);

  const { mutateAsync: selectAddressMutation, isPending: isSelecting } = useSelectDeliveryAddress();
  const { mutateAsync: createAddressMutation, isPending: isCreating } = useCreateDeliveryAddress();
  const { mutateAsync: updateAddressMutation, isPending: isUpdating } = useUpdateDeliveryAddress();
  const { mutateAsync: deleteAddressMutation } = useDeleteDeliveryAddress();
  const { mutateAsync: deleteProjectMutation } = useDeleteProject();

  const activeSelectedId = useMemo(() => {
    const d = defaultAddressData?.data?.address || defaultAddressData?.data || defaultAddressData?.address || defaultAddressData;
    return d?._id || d?.delivery_address_id || null;
  }, [defaultAddressData]);

  // --- Map Movement Handlers ---
  const onMapLoad = React.useCallback((map) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);

  if (!isOpen) return null;



    const onMapDragStart = () => {
  setIsMapDragging(true);
};
 
const onMapIdle = async () => {
    if (mapRef.current) {
      setIsMapDragging(false);
      const center = mapRef.current.getCenter();
      if (!center) return;
      
      const lat = center.lat();
      const lng = center.lng();
      
      // Threshold check: Only reverse geocode if map moved significantly (prevents infinite render loops)
      /* if (Math.abs(lat - exactCoords.latitude) > 0.0001 || Math.abs(lng - exactCoords.longitude) > 0.0001) {
        setExactCoords({ latitude: lat, longitude: lng });
        await performReverseGeocode(lat, lng);
      } */

      // Update location data only if the map was actually dragged to a new coordinate
      if (Math.abs(lat - mapCenter.lat) > 0.00005 || Math.abs(lng - mapCenter.lng) > 0.00005) {
        setMapCenter({ lat, lng });
        await performReverseGeocode(lat, lng);
      }
    }
  };

  const handleSelectAddress = async (addressId) => {
    setErrorMessage('');
    try {
      await selectAddressMutation({
        customer_id: customerId,
        delivery_address_id: addressId,
      });
      onClose();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || 'Could not set the delivery address.');
    }
  };


  const handleStartAdd = () => {
    setEditingAddress(null);
    setFormData({
      addressType: 'project',
      projectName: '',
      completeAddress: '',
      building: '',
      landmark: '',
      city: '',
      pincode: '',
      contactName: customer?.customer_name || '',
      contactMobile: customer?.mobile_number || '',
    });
    setViewMode('FORM');
  };

  const handleStartEdit = (e, addr) => {
    e.stopPropagation();
    setEditingAddress(addr);
    setFormData({
      addressType: addr.address_type === 'project' ? 'project' : 'other',
      projectName: addr.project_name || '',
      completeAddress: addr.area_sector_locality || addr.complete_address || '',
      building: addr.building || addr.building_name || '',
      landmark: addr.landmark || '',
      city: addr.city || '',
      pincode: addr.pincode || '',
      contactName: addr.contact_name || addr.contact_person_name || '',
      contactMobile: addr.contact_mobile || addr.contact_number || '',
    });
    setViewMode('FORM');
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    const isProject = item.address_type === 'project' && item.project_id;
    const confirmMessage = isProject
      ? 'Are you sure you want to delete this project and its linked delivery address?'
      : 'Are you sure you want to delete this delivery address?';

    if (!window.confirm(confirmMessage)) return;

    try {
      if (isProject) {
        const pId = typeof item.project_id === 'object' ? item.project_id._id : item.project_id;
        await deleteProjectMutation(pId);
      }
      await deleteAddressMutation(item._id);
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses', customerId] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', customerId] });
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || 'Could not delete address.');
    }
  };

  // --- Web Location Services ---
  const handleSearchInput = (e) => {
    const text = e.target.value;
    setSearchQuery(text);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (text.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Fallback to fetch if LocationService isn't fully adapted for web[cite: 8]
        const results = await LocationService.searchPlaces(text, 'web_session_123'); 
        setSearchResults(Array.isArray(results) ? results : []);
      } catch (err) {
        console.warn("Search failed", err);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };


/*   const handleSubmitForm = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (formData.addressType === 'project' && !formData.projectName.trim()) {
      setErrorMessage('Please enter the project / site name.');
      return;
    }
    if (!formData.completeAddress.trim()) {
      setErrorMessage('Please enter the street / site address.');
      return;
    }

    const payload = {
      customer_id: customerId,
      hubId: hubId,
      address_type: formData.addressType,
      project_name: formData.addressType === 'project' ? formData.projectName : '',
      area_sector_locality: formData.completeAddress,
      complete_address: formData.completeAddress,
      building: formData.building,
      landmark: formData.landmark,
      city: formData.city || 'Vijayawada',
      pincode: formData.pincode,
      contact_name: formData.contactName,
      contact_mobile: formData.contactMobile,
      is_default: true,
      is_active: true,
      latitude: 16.5062, // Replace with dynamic geocoding if available
      longitude: 80.648,
    };

    try {
      if (editingAddress?._id) {
        await updateAddressMutation({
          addressId: editingAddress._id,
          payload,
        });
      } else {
        await createAddressMutation(payload);
      }
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses', customerId] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress', customerId] });
      setViewMode('LIST');
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || 'Failed to save address.');
    }
  }; */

  const handleSelectSearchResult = async (place) => {
    setSearchResults([]);
    setSearchQuery(place.description || place.formatted_address || '');
    setTempAddressData(prev => ({ ...prev, isFetching: true }));
    setViewMode('MAP');
    try {
      const details = await LocationService.getPlaceDetails(place.place_id, 'web_session_123');
      const loc = details?.geometry?.location;
      if (loc) {
        setExactCoords({ latitude: loc.lat, longitude: loc.lng });
         setMapCenter({ lat: loc.lat, lng: loc.lng });
        // Programmatically pan the map (Prevents React center prop conflict)
        if (mapRef.current) {
          mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
          mapRef.current.setZoom(17);
        }
        
        await performReverseGeocode(loc.lat, loc.lng);
      }
    } catch (err) {
      console.warn(err);
      setTempAddressData(prev => ({ ...prev, isFetching: false }));
    }
  };

  const handleUseCurrentLocation = () => {
    setViewMode('MAP');
    setIsLocating(true);
    setTempAddressData(prev => ({ ...prev, isFetching: true }));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setExactCoords({ latitude, longitude });
          setMapCenter({ lat: latitude, lng: longitude });
          // Programmatically pan the map to GPS location
          if (mapRef.current) {
            mapRef.current.panTo({ lat: latitude, lng: longitude });
            mapRef.current.setZoom(17);
          }
          
          await performReverseGeocode(latitude, longitude);
          setIsLocating(false);
        },
        (error) => {
          alert("Location access denied or unavailable.");
          setIsLocating(false);
          setTempAddressData(prev => ({ ...prev, isFetching: false }));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
      setIsLocating(false);
    }
  };

 const performReverseGeocode = async (lat, lng) => {
    setTempAddressData(prev => ({ ...prev, isFetching: true }));
    try {
      const backendData = await LocationService.reverseGeocode(lat, lng);
      const components = backendData.components || backendData.address_components || [];
      
      const city = components.find(c => c.types.includes('locality'))?.long_name || 'Selected Location';
      const fullAddress = backendData.formatted_address || backendData.address || 'Address selected on map';
      const pincode = components.find(c => c.types.includes('postal_code'))?.long_name || '';

      setTempAddressData({ city, fullAddress, isFetching: false });
      setFormData(prev => ({ ...prev, city, completeAddress: fullAddress, pincode }));
    } catch (err) {
      setTempAddressData({ city: 'Unknown', fullAddress: 'Coordinates: ' + lat + ', ' + lng, isFetching: false });
    }
  };

  const proceedToForm = () => {
    if (!tempAddressData.fullAddress) return;
    setFormData(prev => ({ ...prev, completeAddress: tempAddressData.fullAddress }));
    setViewMode('FORM');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const payload = {
      customer_id: customerId,
      hubId: hubId,
      address_type: formData.addressType,
      project_name: formData.addressType === 'project' ? formData.projectName : '',
      area_sector_locality: formData.completeAddress,
      complete_address: formData.completeAddress,
      building: formData.building,
      landmark: formData.landmark,
      city: formData.city || 'Vijayawada',
      pincode: formData.pincode,
      contact_name: formData.contactName,
      contact_mobile: formData.contactMobile,
      is_default: true,
      is_active: true,
      latitude: mapCenter.lat || exactCoords.latitude, // Captured from Map view
      longitude: mapCenter.lng || exactCoords.longitude, // Captured from Map view
    };

    try {
      if (editingAddress?._id) {
        await updateAddressMutation({ addressId: editingAddress._id, payload });
      } else {
        await createAddressMutation(payload);
      }
      setViewMode('LIST');
    } catch (err) {
      setErrorMessage('Failed to save address.');
    }
  };

/*   const filteredAddresses = (Array.isArray(addressesList) ? addressesList : []).filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const pName = (a.project_name || '').toLowerCase();
    const addr = (a.area_sector_locality || a.complete_address || '').toLowerCase();
    return pName.includes(q) || addr.includes(q);
  }); */
  //console.log('addressesList', addressesList)
  const filteredAddresses = addressesList.filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (a.project_name || '').toLowerCase().includes(q) || (a.area_sector_locality || '').toLowerCase().includes(q);
  });

  const isSaving = isCreating || isUpdating;

return (
    <>
      <style>{`
        .modal-overlay {
          position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.5); z-index: 99999;
          display: flex; align-items: center; justify-content: center; backdrop-filter: blur(3px);
        }
        .modal-container {
          background-color: #FFFFFF; width: 100%; height: 100%; display: flex; flex-direction: column; overflow: hidden; position: relative;
        }
        @media (min-width: 640px) {
          .modal-container { max-width: 480px; height: 85vh; border-radius: 16px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2); }
          .mobile-back-btn { display: none; }
          .desktop-close-btn { display: block !important; }
        }
        .header-bar {
          display: flex; align-items: center; justify-content: space-between; padding: 16px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.05); z-index: 20;
        }
        .header-title { font-size: 18px; font-weight: 700; color: #130022; flex: 1; margin-left: 12px; }
        .desktop-close-btn { display: none; background: none; border: none; cursor: pointer; }
        .mobile-back-btn { background: none; border: none; cursor: pointer; padding: 4px; }
        .content-scroll { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 20px; }
        
        /* Zepto Map View Styles */
        .map-view-wrapper { flex: 1; display: flex; flex-direction: column; background: #F8FAFC; position: relative; }
        .map-container { flex: 1; position: relative; overflow: hidden; }
        
        /* Search Box Over Map */
        .map-search-float { position: absolute; top: 16px; left: 16px; right: 16px; z-index: 10; }
        .search-input-wrapper { display: flex; align-items: center; background: #FFFFFF; border: 1px solid rgba(226, 232, 240, 0.8); border-radius: 8px; padding: 0 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .search-input-box { flex: 1; border: none; padding: 14px 8px; font-size: 14px; outline: none; background: transparent; color: #130022; }
        .search-results-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: #FFFFFF; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); margin-top: 6px; max-height: 220px; overflow-y: auto; z-index: 20; border: 1px solid #F1F5F9; }
        .search-result-item { padding: 14px 16px; border-bottom: 1px solid #F1F5F9; cursor: pointer; font-size: 13.5px; color: #2B2A29; display: flex; flex-direction: column; gap: 4px; }
        .search-result-item:hover { background: #F8FAFC; }
        
        /* Fixed Center Pin Logic */
        .map-center-pin-container {
          position: absolute; top: 50%; left: 50%; z-index: 5; pointer-events: none;
          display: flex; flex-direction: column; align-items: center;
        }
        .pin-wrapper {
          transform: translate(-50%, -100%);
          transition: transform 0.2s cubic-bezier(0.2, 0, 0, 1);
          display: flex; flex-direction: column; align-items: center;
        }
        .pin-dragging {
          transform: translate(-50%, -130%);
        }
        .precision-dot {
          width: 6px; height: 6px; background-color: #E9001E; border-radius: 50%;
          position: absolute; bottom: -3px; left: 50%; transform: translateX(-50%);
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        
        .gps-fab { position: absolute; bottom: 20px; right: 16px; background: #FFFFFF; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px rgba(0,0,0,0.15); z-index: 10; cursor: pointer; border: none; transition: transform 0.15s ease; }
        .gps-fab:active { transform: scale(0.95); }
        
        /* Bottom Panel */
        .bottom-confirmation-panel { background: #FFFFFF; padding: 14px 20px 24px 20px; box-shadow: 0 -4px 20px rgba(0,0,0,0.08); z-index: 10; border-top-left-radius: 20px; border-top-right-radius: 20px; }
        .location-title { font-size: 17px; font-weight: 800; color: #130022; margin: 0 0 4px 0; }
        .location-subtitle { font-size: 13px; color: #707B8C; margin: 0 0 16px 0; }
        .address-preview-box { border: 1px solid #E2E8F0; border-radius: 12px; padding: 8px 14px; display: flex; alignItems: center; margin-bottom: 10px; background: #F8FAFC; }
        
        /* Action Rows & Forms */
        .action-row { display: flex; align-items: center; padding: 14px 0; cursor: pointer; border-bottom: 1px solid #F1F5F9; }
        .action-icon-box { width: 32px; height: 32px; border-radius: 50%; background: #FFF1F2; display: flex; align-items: center; justify-content: center; margin-right: 14px; }
        .action-text { font-size: 14px; font-weight: 700; color: #E9001E; }
        .saved-title { font-size: 16px; font-weight: 800; color: #130022; margin-bottom: 12px; }
        
        .zepto-btn-primary { width: 100%; background-color: #E9001E; color: #FFFFFF; border: none; padding: 16px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .zepto-btn-primary:disabled { background-color: #FCA5A5; cursor: not-allowed; }
      `}</style>

      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          
          <div className="header-bar">
            <button className="mobile-back-btn" onClick={() => viewMode !== 'LIST' ? setViewMode('LIST') : onClose()}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#130022" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15.5 19L8.5 12L15.5 5"></path></svg>
            </button>
            <span className="header-title">
              {viewMode === 'LIST' ? 'Location Information' : viewMode === 'MAP' ? 'Select Location' : 'Enter Details'}
            </span>
            <button className="desktop-close-btn" onClick={onClose}>
              <img src={X} alt="close" style={{ width: 18, height: 18 }} />
            </button>
          </div>

          {viewMode === 'LIST' && (
            <div className="content-scroll">
               
              <div style={{ position: 'relative', zIndex: 30, marginBottom: '8px' }}>
             <div className="search-input-wrapper" style={{ border: '1px solid #E2E8F0', background: '#F8FAFC'}}>
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="#858585" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
  
  <input 
    type="text" 
    className="search-input-box" 
    placeholder="Search a new address" 
    value={searchQuery}
    onChange={handleSearchInput}
  />
</div>
                {searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((res, i) => (
                      <div key={i} className="search-result-item" onClick={() => {
                        setViewMode('MAP'); 
                        handleSelectSearchResult(res);
                      }}>
                        <span style={{ fontWeight: 600 }}>{res.structured_formatting?.main_text || res.description.split(',')[0]}</span>
                        <span style={{ fontSize: 12, color: '#707B8C' }}>{res.structured_formatting?.secondary_text || res.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="action-list-group">
                <div className="action-row" onClick={handleUseCurrentLocation}>
                  <div className="action-icon-box">
                    <img src={MapPin} alt="Target" style={{ width: 16, height: 16, filter: 'invert(16%) sepia(87%) saturate(5838%) hue-rotate(343deg) brightness(98%) contrast(106%)' }} />
                  </div>
                  <span className="action-text">Use My Current Location</span>
                </div>
                <div className="action-row" onClick={() => setViewMode('MAP')} style={{ borderBottom: 'none' }}>
                  <div className="action-icon-box">
                    <img src={Plus} alt="+" style={{ width: 14, height: 14, filter: 'invert(16%) sepia(87%) saturate(5838%) hue-rotate(343deg) brightness(98%) contrast(106%)' }} />
                  </div>
                  <span className="action-text">Add New Address</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#707B8C" strokeWidth="2" style={{ marginLeft: 'auto' }}><path d="M9 18L15 12L9 6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>

              <div className="saved-list">
                <h4 className="saved-title">Saved Addresses</h4>
                {isLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#707B8C' }}>Loading...</div>
                ) : filteredAddresses.map((addr) => {
                    const isSelected = activeSelectedId === addr._id;
                    const isProject = addr.address_type === 'project';
                    return (
                      <div key={addr._id} style={{ border: '1.5px solid', borderRadius: 12, padding: 14, display: 'flex', gap: 14, cursor: 'pointer', marginBottom: 12, borderColor: isSelected ? '#E9001E' : '#E2E8F0', backgroundColor: isSelected ? '#FFF9F9' : '#FFFFFF' }} onClick={() => handleSelectAddress(addr._id)}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={isProject ? BuildingOffice : MapPin} alt="icon" style={{ width: 22, height: 22 }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <strong style={{ fontSize: 14, color: '#130022' }}>{isProject ? addr.project_name : 'Site'}</strong>
                            {isSelected && <span style={{ background: '#DCFCE7', color: '#166534', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>Selected</span>}
                          </div>
                          <p style={{ margin: 0, fontSize: 12.5, color: '#5A6477', marginTop: 4, lineHeight: '1.4' }}>{addr.area_sector_locality}</p>
                        </div>
                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 }} onClick={(e) => handleDelete(e, addr)}>
                          <img src={Trash} alt="delete" style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    );
                })}
              </div>
            </div>
          )}

          {viewMode === 'MAP' && (
            <div className="map-view-wrapper">
              
              <div className="map-search-float">
                <div className="search-input-wrapper">
                  <svg fill="none" height="15" viewBox="0 0 15 15" width="15" xmlns="http://www.w3.org/2000/svg">
                    <path clipRule="evenodd" d="M6.5 2C4.01472 2 2 4.01472 2 6.5C2 8.98528 4.01472 11 6.5 11C8.98528 11 11 8.98528 11 6.5C11 4.01472 8.98528 2 6.5 2ZM1 6.5C1 3.46243 3.46243 1 6.5 1C9.53757 1 12 3.46243 12 6.5C12 9.53757 9.53757 1 6.5 1ZM10.5397 11.0932C10.3502 10.8924 10.3593 10.576 10.5601 10.3864C10.7609 10.1969 11.0773 10.206 11.2669 10.4068L13.864 13.1577C14.0535 13.3585 14.0444 13.675 13.8436 13.8646C13.6428 14.0541 13.3264 14.045 13.1368 13.8442L10.5397 11.0932Z" fill="#858585" fillRule="evenodd"></path>
                  </svg>
                  <input 
                    type="text" 
                    className="search-input-box" 
                    placeholder="Search a new address" 
                    value={searchQuery}
                    onChange={handleSearchInput}
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map((res, i) => (
                      <div key={i} className="search-result-item" onClick={() => handleSelectSearchResult(res)}>
                        <span style={{ fontWeight: 600 }}>{res.structured_formatting?.main_text || res.description.split(',')[0]}</span>
                        <span style={{ fontSize: 12, color: '#707B8C' }}>{res.structured_formatting?.secondary_text || res.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* LIVE MAP RENDERING BLOCK */}
              <div className="map-container">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter || defaultCenter} // STATIC reference to prevent react re-renders from cancelling drag
                    zoom={15}
                    options={{
                      disableDefaultUI: true, // Hides map/satellite buttons
                      zoomControl: false,
                      gestureHandling: 'greedy'
                    }}
                    onLoad={onMapLoad}
                    onDragStart={onMapDragStart}
                    onIdle={onMapIdle}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading Map...
                  </div>
                )}
                
                {/* Fixed Center Pin */}
                <div className="map-center-pin-container">
                  <div className={`pin-wrapper ${isMapDragging ? 'pin-dragging' : ''}`}>
                    <img alt="Location Pin" loading="lazy" width="48" height="48" src="https://cdn.zeptonow.com/web-static-assets-prod/artifacts/17.0.1/images/google-map/location-pin.svg" />
                    <div className="precision-dot" />
                  </div>
                </div>
                
                <button className="gps-fab" onClick={handleUseCurrentLocation} disabled={isLocating} aria-label="Current Location">
                  {isLocating ? (
                     <div style={{ width: 18, height: 18, border: '2px solid #E9001E', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#5A6477" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 8V16" stroke="#5A6477" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 12H16" stroke="#5A6477" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>

              <div className="bottom-confirmation-panel">
                <div className="address-preview-box">
                  <img src={MapPin} alt="pin" style={{ width: 22, height: 20, opacity: 0.7, marginRight: 12, marginTop: 2, alignSelf: 'flex-start' }} />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, margin: 0, color: '#130022' }}>
                      {tempAddressData.isFetching ? 'Locating...' : (tempAddressData.city || 'Pin Location')}
                    </h4>
                    <p style={{ fontSize: 11, color: '#5A6477', margin: '4px 0 0 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>
                      {tempAddressData.fullAddress || 'Move the map to set your delivery location precisely'}
                    </p>
                  </div>
                </div>

                <button className="zepto-btn-primary" onClick={proceedToForm} disabled={tempAddressData.isFetching || !tempAddressData.fullAddress}>
                  Confirm Location
                </button>
              </div>
            </div>
          )}

          {viewMode === 'FORM' && (
            <form className="content-scroll" onSubmit={handleSubmitForm}>
              <div style={{ background: '#F8FAFC', padding: 14, borderRadius: 12, marginBottom: 12, border: '1px solid #F1F5F9' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#707B8C', letterSpacing: 0.5 }}>DELIVERING TO</span>
                <p style={{ fontSize: 15, fontWeight: 800, color: '#130022', margin: '6px 0 2px 0' }}>{tempAddressData.city}</p>
                <p style={{ fontSize: 13, color: '#5A6477', margin: 0, lineHeight: '1.4' }}>{tempAddressData.fullAddress}</p>
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                <button type="button" onClick={() => setFormData({ ...formData, addressType: 'project' })} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid', borderColor: formData.addressType === 'project' ? '#E9001E' : '#E2E8F0', background: formData.addressType === 'project' ? '#FFF1F2' : '#FFFFFF', color: formData.addressType === 'project' ? '#E9001E' : '#5A6477', fontWeight: 700, fontSize: 13 }}>Project / Site</button>
                <button type="button" onClick={() => setFormData({ ...formData, addressType: 'other' })} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid', borderColor: formData.addressType === 'other' ? '#E9001E' : '#E2E8F0', background: formData.addressType === 'other' ? '#FFF1F2' : '#FFFFFF', color: formData.addressType === 'other' ? '#E9001E' : '#5A6477', fontWeight: 700, fontSize: 13 }}>Home / Other</button>
              </div>

              {formData.addressType === 'project' && (
                <input type="text" placeholder="Project / Site Name *" required value={formData.projectName} onChange={e => setFormData({...formData, projectName: e.target.value})} style={{ border: '1px solid #CBD5E1', borderRadius: 10, padding: '14px', width: '100%', outline: 'none', fontSize: 14, color: '#130022' }} />
              )}
              
              <input type="text" placeholder="Complete Flat / Building / Street *" required value={formData.completeAddress} onChange={e => setFormData({...formData, completeAddress: e.target.value})} style={{ border: '1px solid #CBD5E1', borderRadius: 10, padding: '14px', width: '100%', outline: 'none', fontSize: 14, color: '#130022' }} />
              <input type="text" placeholder="Landmark (Optional)" value={formData.landmark} onChange={e => setFormData({...formData, landmark: e.target.value})} style={{ border: '1px solid #CBD5E1', borderRadius: 10, padding: '14px', width: '100%', outline: 'none', fontSize: 14, color: '#130022' }} />
              
              <div style={{ display: 'flex', gap: 12 }}>
                <input type="text" placeholder="Contact Name *" required value={formData.contactName} onChange={e => setFormData({...formData, contactName: e.target.value})} style={{ border: '1px solid #CBD5E1', borderRadius: 10, padding: '14px', width: '100%', outline: 'none', fontSize: 14, color: '#130022' }} />
                <input type="text" placeholder="Mobile Number *" required value={formData.contactMobile} onChange={e => setFormData({...formData, contactMobile: e.target.value})} style={{ border: '1px solid #CBD5E1', borderRadius: 10, padding: '14px', width: '100%', outline: 'none', fontSize: 14, color: '#130022' }} />
              </div>

              <div style={{ marginTop: 'auto', paddingTop: 24, paddingBottom: 16 }}>
                <button type="submit" className="zepto-btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving Address...' : 'Save Address'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </>
  );
}

const styles = {
  // Retaining isolated element styles for dynamic rendering internally
  errorAlert: { backgroundColor: '#FEF2F2', borderBottom: '1px solid #FCA5A5', color: '#B91C1C', padding: '10px 16px', fontSize: 13, fontWeight: '600' },
  centerState: { padding: '40px 0', textAlign: 'center', color: '#707B8C', fontSize: 13.5 },
  addressCard: { border: '1.5px solid #E2E8F0', borderRadius: 12, padding: 14, display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer', marginBottom: 12 },
  addressCardSelected: { borderColor: '#E9001E', backgroundColor: '#FFF9F9' },
  cardIconCol: { width: 36, height: 36, borderRadius: 8, backgroundColor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardContentCol: { flex: 1, display: 'flex', flexDirection: 'column', gap: 3 },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#2B2A29' },
  selectedBadge: { backgroundColor: '#DCFCE7', color: '#166534', fontSize: 10.5, fontWeight: '700', borderRadius: 4, padding: '2px 6px', display: 'flex', gap: 4 },
  cardAddressText: { margin: 0, fontSize: 12.5, color: '#475569', lineHeight: '17px' },
  cardActionsCol: { display: 'flex', alignItems: 'center', gap: 8 },
  actionLinkBtn: { background: 'none', border: 'none', color: '#2B2A29', fontSize: 12, fontWeight: '700', cursor: 'pointer' },
  deleteIconBtn: { background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 },
  formContainer: { display: 'flex', flexDirection: 'column', gap: 14 },
  typeSelectorRow: { display: 'flex', gap: 10 },
  typeChoiceBtn: { flex: 1, padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 8, backgroundColor: '#FFFFFF', fontSize: 12.5, fontWeight: '600', color: '#64748B', cursor: 'pointer' },
  typeChoiceActive: { borderColor: '#E9001E', backgroundColor: '#FFF5F5', color: '#E9001E', fontWeight: '700' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  inputLabel: { fontSize: 12, fontWeight: '700', color: '#2B2A29' },
  inputField: { border: '1px solid #CBD5E1', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' },
  twoColRow: { display: 'flex', gap: 12 },
  formButtonRow: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 },
  formCancelBtn: { border: '1px solid #CBD5E1', backgroundColor: '#FFFFFF', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: '600', cursor: 'pointer' },
  formSubmitBtn: { backgroundColor: '#E9001E', border: 'none', color: '#FFFFFF', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: '700', cursor: 'pointer' },
};